import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Download, Trash2, Loader2, Eye, X, BarChart3, Plus, Link2, Copy,
  Users, AlertTriangle, Star, Flame, ThermometerSun, Snowflake, Sprout,
  ChevronDown, ChevronUp, Send, Mail, CheckCircle
} from "lucide-react";

const SALES_Q_LABELS = {
  q1: "Q1: Lead Quality", q2: "Q2: Drop-off Stage", q3: "Q3: Active Prospects",
  q4: "Q4: Common Objection", q4_other: "Q4: Other",
  q5: "Q5: Pricing Confidence", q6: "Q6: Lost to Competitor?", q6_details: "Q6: Details",
  q7: "Q7: KDM Difficulty", q8: "Q8: Demo Customization",
  q9: "Q9: Least Confident Product", q9_details: "Q9: Details",
  q10: "Q10: Training", q11: "Q11: Case Studies?", q11_details: "Q11: Missing",
  q12: "Q12: Followed Steps?", q12_details: "Q12: Skipped",
  q13: "Q13: Non-Selling Time", q14: "Q14: Proposal Turnaround",
  q15: "Q15: CRM Usefulness", q15_details: "Q15: CRM Suggestion",
  q16: "Q16: Mgmt Support", q17: "Q17: Reviews", q18: "Q18: Incentives",
  q19: "Q19: Hardest Part", q20: "Q20: One Change", q20_details: "Q20: Detail", q21: "Q21: Open Feedback",
};
const CUSTOMER_Q_LABELS = {
  q1: "Q1: Demo Experience", q2: "Q2: Relevance", q3: "Q3: Buying Intent",
  q4: "Q4: Timeline", q5: "Q5: Blockers?", q5_blockers: "Q5: Blocker Details",
  q5_other: "Q5: Other", q6: "Q6: Consultant Rating", q7: "Q7: Next Step", q7_details: "Q7: Topic",
};
const SALES_RATING_KEYS = ["q1", "q5", "q7", "q10", "q15", "q17"];
const CUSTOMER_RATING_KEYS = ["q1", "q6"];

const RatingBadge = ({ value }) => {
  const num = parseInt(value);
  if (isNaN(num)) return <span className="text-slate-400 text-sm">—</span>;
  const colors = ["", "bg-red-100 text-red-700", "bg-orange-100 text-orange-700", "bg-amber-100 text-amber-700", "bg-emerald-100 text-emerald-700", "bg-green-100 text-green-700"];
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold ${colors[num] || "bg-slate-100"}`}><Star className="w-3 h-3" /> {num}/5</span>;
};

const IntentBadge = ({ intent }) => {
  const c = { hot: { l: "HOT", i: Flame, c: "bg-red-100 text-red-700" }, warm: { l: "WARM", i: ThermometerSun, c: "bg-amber-100 text-amber-700" }, nurture: { l: "NURTURE", i: Sprout, c: "bg-blue-100 text-blue-700" }, cool: { l: "COOL", i: Snowflake, c: "bg-slate-100 text-slate-600" } }[intent] || { l: intent?.toUpperCase() || "—", i: BarChart3, c: "bg-slate-100 text-slate-600" };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold ${c.c}`}><c.i className="w-3 h-3" /> {c.l}</span>;
};

export default function AdminFeedback() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [campaignResponses, setCampaignResponses] = useState({});
  const [viewFeedback, setViewFeedback] = useState(null);
  const [viewType, setViewType] = useState("sales");
  const [linkModal, setLinkModal] = useState(null); // { url, title, users }

  // Create form state
  const [newTitle, setNewTitle] = useState("");
  const [newType, setNewType] = useState("sales");
  const [allUsers, setAllUsers] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCampaigns(); }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/feedback/campaigns");
      setCampaigns(res.data);
    } catch { toast.error("Failed to load campaigns"); }
    setLoading(false);
  };

  const loadUsers = useCallback(async (type) => {
    try {
      const res = await api.get("/users");
      const users = res.data || [];
      if (type === "sales") {
        setAllUsers(users.filter((u) => u.user_type !== "external" || u.role === "admin"));
      } else {
        setAllUsers(users.filter((u) => u.user_type === "external"));
      }
    } catch { toast.error("Failed to load users"); }
  }, []);

  const handleOpenCreate = async () => {
    setShowCreate(true);
    setNewTitle("");
    setNewType("sales");
    setSelectedEmails([]);
    setUserSearch("");
    setSearchFocused(false);
    await loadUsers("sales");
  };

  const handleTypeChange = async (type) => {
    setNewType(type);
    setSelectedEmails([]);
    setUserSearch("");
    await loadUsers(type);
  };

  const toggleEmail = (email) => {
    setSelectedEmails((prev) => prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]);
  };

  const selectAll = () => {
    if (selectedEmails.length === allUsers.length) setSelectedEmails([]);
    else setSelectedEmails(allUsers.map((u) => u.email));
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) { toast.error("Title is required"); return; }
    if (selectedEmails.length === 0) { toast.error("Select at least one user"); return; }
    setCreating(true);
    try {
      const baseUrl = window.location.origin;
      const res = await api.post("/admin/feedback/campaigns", {
        title: newTitle, type: newType, selected_users: selectedEmails, base_url: baseUrl,
      });
      toast.success(res.data.message);
      setShowCreate(false);
      loadCampaigns();

      // Show the generated link to admin
      const feedbackUrl = res.data.feedback_url || `${baseUrl}/feedback/${res.data.token}`;
      const users = selectedEmails.map((email) => {
        const u = allUsers.find((usr) => usr.email === email);
        return { email, name: u?.name || email.split("@")[0] };
      });
      setLinkModal({ url: feedbackUrl, title: newTitle, token: res.data.token, users });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create campaign");
    }
    setCreating(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this campaign and all its responses?")) return;
    try {
      await api.delete(`/admin/feedback/campaigns/${id}`);
      toast.success("Campaign deleted");
      loadCampaigns();
    } catch { toast.error("Failed to delete"); }
  };

  const handleExpand = async (campaignId) => {
    if (expandedCampaign === campaignId) { setExpandedCampaign(null); return; }
    setExpandedCampaign(campaignId);
    if (!campaignResponses[campaignId]) {
      try {
        const res = await api.get(`/admin/feedback/campaigns/${campaignId}/responses`);
        setCampaignResponses((prev) => ({ ...prev, [campaignId]: res.data }));
      } catch { toast.error("Failed to load responses"); }
    }
  };

  const handleExport = async (campaignId) => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/feedback/campaigns/${campaignId}/export/excel`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Feedback_Export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch { toast.error("Export failed"); }
    setExporting(false);
  };

  const safeCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fallback for when clipboard API is blocked
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.cssText = "position:fixed;left:-9999px;top:-9999px";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); document.body.removeChild(ta); return true; }
      catch { document.body.removeChild(ta); return false; }
    }
  };

  const copyLink = (token, campaign) => {
    const url = `${window.location.origin}/feedback/${token}`;
    const users = (campaign?.users || []).map((u) => ({ email: u.email, name: u.name || u.email.split("@")[0] }));
    setLinkModal({ url, title: campaign?.title || "Feedback", token, users });
  };

  const handleCopyUrl = async (url) => {
    const ok = await safeCopy(url);
    if (ok) toast.success("Link copied to clipboard");
    else toast.error("Could not copy — please select and copy manually");
  };

  const deleteResponse = async (feedbackId, type, campaignId) => {
    if (!window.confirm("Delete this response?")) return;
    try {
      await api.delete(`/admin/feedback/responses/${feedbackId}?feedback_type=${type}`);
      toast.success("Response deleted");
      const res = await api.get(`/admin/feedback/campaigns/${campaignId}/responses`);
      setCampaignResponses((prev) => ({ ...prev, [campaignId]: res.data }));
      loadCampaigns();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <AdminLayout title="Feedback Management">
      <div className="space-y-6" data-testid="admin-feedback-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Feedback Management</h1>
            <p className="text-sm text-slate-500 mt-1">Create feedback campaigns, generate links, and track responses</p>
          </div>
          <Button onClick={handleOpenCreate} className="gap-2 bg-[#0066CC] hover:bg-[#0052A3]" data-testid="create-campaign-btn">
            <Plus className="w-4 h-4" /> New Campaign
          </Button>
        </div>

        {/* Create Campaign Dialog */}
        {showCreate && (
          <div className="bg-white rounded-2xl border-2 border-blue-200 p-6 shadow-lg space-y-5" data-testid="create-campaign-form">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">Create Feedback Campaign</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowCreate(false)}><X className="w-5 h-5" /></Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Campaign Title *</Label>
                <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder='e.g., "Q4 Sales Review" or "Post Demo - ABC Corp"' className="mt-1" data-testid="campaign-title-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Feedback Type *</Label>
                <div className="flex gap-2 mt-1">
                  {[{ id: "sales", label: "Sales Feedback", desc: "Internal users" }, { id: "customer", label: "Customer Feedback", desc: "External users" }].map((t) => (
                    <button key={t.id} onClick={() => handleTypeChange(t.id)}
                      className={`flex-1 p-3 rounded-xl border-2 text-left transition-all ${newType === t.id ? "border-[#0066CC] bg-blue-50" : "border-slate-200 hover:border-blue-200"}`}
                      data-testid={`type-${t.id}`}>
                      <p className={`text-sm font-semibold ${newType === t.id ? "text-[#0066CC]" : "text-slate-700"}`}>{t.label}</p>
                      <p className="text-xs text-slate-400">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* User Selection - Search & Add */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Select Users ({selectedEmails.length} selected)</Label>
                <Button variant="outline" size="sm" onClick={selectAll} className="text-xs" data-testid="select-all-users-btn">
                  {selectedEmails.length === allUsers.length && allUsers.length > 0 ? "Deselect All" : "Select All"}
                </Button>
              </div>

              {/* Selected users as removable chips */}
              {selectedEmails.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3 p-3 bg-blue-50/60 rounded-xl border border-blue-100" data-testid="selected-users-chips">
                  {selectedEmails.map((email) => {
                    const u = allUsers.find((usr) => usr.email === email);
                    return (
                      <span key={email} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 bg-white border border-blue-200 rounded-lg text-xs font-medium text-slate-700 shadow-sm">
                        {u?.name || email.split("@")[0]}
                        <button onClick={() => toggleEmail(email)} className="ml-0.5 p-0.5 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors" data-testid={`remove-user-${email}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Search input */}
              <div className="relative" data-testid="user-search-container">
                <Input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                  placeholder="Search by name or email..."
                  className="pr-8"
                  data-testid="user-search-input"
                />
                {userSearch && (
                  <button onClick={() => setUserSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                  </button>
                )}

                {/* Dropdown results */}
                {searchFocused && (() => {
                  const q = userSearch.toLowerCase().trim();
                  const filtered = allUsers.filter((u) => {
                    if (selectedEmails.includes(u.email)) return false;
                    if (!q) return true;
                    return (u.name || "").toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
                  });
                  if (allUsers.length === 0) return (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg p-4">
                      <p className="text-sm text-slate-400 text-center">No {newType === "sales" ? "internal" : "external"} users found</p>
                    </div>
                  );
                  if (filtered.length === 0) return (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg p-4">
                      <p className="text-sm text-slate-400 text-center">{q ? "No matching users" : "All users already selected"}</p>
                    </div>
                  );
                  return (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-52 overflow-y-auto" data-testid="user-search-results">
                      {filtered.map((u) => (
                        <button key={u.email} onMouseDown={(e) => { e.preventDefault(); toggleEmail(u.email); setUserSearch(""); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-left transition-colors border-b border-slate-50 last:border-0"
                          data-testid={`add-user-${u.email}`}>
                          <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0">
                            {(u.name || u.email)[0].toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{u.name || u.email.split("@")[0]}</p>
                            <p className="text-xs text-slate-400 truncate">{u.email}</p>
                          </div>
                          <Plus className="w-4 h-4 text-blue-500 ml-auto shrink-0" />
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
              <Button onClick={handleCreate} disabled={creating} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="create-campaign-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {creating ? "Creating..." : "Create & Send Emails"}
              </Button>
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <Mail className="w-3.5 h-3.5" /> Emails will be sent to selected users with the feedback link
              </div>
            </div>
          </div>
        )}

        {/* Campaign List */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0066CC]" /></div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
            <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No feedback campaigns yet</p>
            <p className="text-sm text-slate-400 mt-1">Create your first campaign to start collecting feedback</p>
          </div>
        ) : (
          <div className="space-y-4">
            {campaigns.map((c) => {
              const isExpanded = expandedCampaign === c.id;
              const respData = campaignResponses[c.id];

              return (
                <div key={c.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`campaign-${c.id}`}>
                  {/* Campaign Header */}
                  <div className="p-5 flex items-center justify-between">
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${c.type === "sales" ? "bg-amber-100" : "bg-teal-100"}`}>
                        <Users className={`w-5 h-5 ${c.type === "sales" ? "text-amber-600" : "text-teal-600"}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-base font-bold text-slate-900 truncate">{c.title}</h3>
                          <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${c.type === "sales" ? "bg-amber-100 text-amber-700" : "bg-teal-100 text-teal-700"}`}>
                            {c.type === "sales" ? "Sales" : "Customer"}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <span>{new Date(c.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                          <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> {c.response_count}/{c.total_users} responses</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => copyLink(c.token, c)} className="gap-1" data-testid={`copy-link-${c.id}`}>
                        <Copy className="w-3.5 h-3.5" /> Link
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExport(c.id)} disabled={exporting || c.response_count === 0} className="gap-1" data-testid={`export-${c.id}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExpand(c.id)} className="gap-1" data-testid={`expand-${c.id}`}>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(c.id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-campaign-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Responses */}
                  {isExpanded && (
                    <div className="border-t border-slate-200 p-5 bg-slate-50">
                      {/* User status list */}
                      <div className="mb-4">
                        <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Invited Users</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {(respData?.campaign?.users || c.users || []).map((u) => (
                            <span key={u.email} className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${u.submitted ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                              {u.submitted ? <CheckCircle className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
                              {u.name || u.email}
                              {!u.submitted && (
                                <button onClick={() => handleCopyUrl(`${window.location.origin}/feedback/${c.token}?email=${encodeURIComponent(u.email)}`)}
                                  className="ml-1 p-0.5 rounded hover:bg-slate-300 transition-colors" title={`Copy link for ${u.email}`} data-testid={`copy-inline-${u.email}`}>
                                  <Copy className="w-2.5 h-2.5" />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Response table */}
                      {!respData ? (
                        <Loader2 className="w-5 h-5 animate-spin text-[#0066CC] mx-auto" />
                      ) : respData.responses?.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-6">No responses yet</p>
                      ) : c.type === "sales" ? (
                        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                          <table className="w-full text-sm" data-testid="sales-responses-table">
                            <thead><tr className="bg-slate-50 border-b">
                              <th className="text-left p-3 font-semibold text-slate-700">Name</th>
                              <th className="text-left p-3 font-semibold text-slate-700">Email</th>
                              <th className="text-center p-3 font-semibold text-slate-700">Quarter</th>
                              <th className="text-center p-3 font-semibold text-slate-700">Lead Quality</th>
                              <th className="text-left p-3 font-semibold text-slate-700">Submitted</th>
                              <th className="text-right p-3 font-semibold text-slate-700">Actions</th>
                            </tr></thead>
                            <tbody>
                              {respData.responses.map((r) => (
                                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="p-3 font-medium">{r.user_name || "—"}</td>
                                  <td className="p-3 text-slate-600">{r.user_email}</td>
                                  <td className="p-3 text-center"><span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{r.quarter_most_active || "—"}</span></td>
                                  <td className="p-3 text-center"><RatingBadge value={r.responses?.q1} /></td>
                                  <td className="p-3 text-xs text-slate-500">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString("en-IN") : "—"}</td>
                                  <td className="p-3 text-right flex gap-1 justify-end">
                                    <Button variant="outline" size="sm" onClick={() => { setViewFeedback(r); setViewType("sales"); }}><Eye className="w-3.5 h-3.5" /></Button>
                                    <Button variant="outline" size="sm" className="text-red-600" onClick={() => deleteResponse(r.id, "sales", c.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                          <table className="w-full text-sm" data-testid="customer-responses-table">
                            <thead><tr className="bg-slate-50 border-b">
                              <th className="text-left p-3 font-semibold text-slate-700">Prospect</th>
                              <th className="text-left p-3 font-semibold text-slate-700">Company</th>
                              <th className="text-center p-3 font-semibold text-slate-700">Intent</th>
                              <th className="text-center p-3 font-semibold text-slate-700">Demo Rating</th>
                              <th className="text-left p-3 font-semibold text-slate-700">Submitted</th>
                              <th className="text-right p-3 font-semibold text-slate-700">Actions</th>
                            </tr></thead>
                            <tbody>
                              {respData.responses.map((r) => (
                                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="p-3"><div className="font-medium">{r.prospect_name || r.user_name || "—"}</div><div className="text-xs text-slate-400">{r.user_email}</div></td>
                                  <td className="p-3 text-slate-600">{r.company_name || "—"}</td>
                                  <td className="p-3 text-center"><IntentBadge intent={r.lead_intent} /></td>
                                  <td className="p-3 text-center"><RatingBadge value={r.responses?.q1} /></td>
                                  <td className="p-3 text-xs text-slate-500">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString("en-IN") : "—"}</td>
                                  <td className="p-3 text-right flex gap-1 justify-end">
                                    <Button variant="outline" size="sm" onClick={() => { setViewFeedback(r); setViewType("customer"); }}><Eye className="w-3.5 h-3.5" /></Button>
                                    <Button variant="outline" size="sm" className="text-red-600" onClick={() => deleteResponse(r.id, "customer", c.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Link Modal */}
        {linkModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setLinkModal(null)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="link-modal">
              <div className="flex items-center justify-between p-5 border-b">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Link2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{linkModal.title}</h3>
                    <p className="text-xs text-slate-500">Feedback link &amp; per-user URLs</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setLinkModal(null)}><X className="w-5 h-5" /></Button>
              </div>
              <div className="p-5 space-y-5">
                {/* Main campaign link */}
                <div>
                  <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Campaign Link</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input readOnly value={linkModal.url} className="text-sm bg-slate-50 font-mono" data-testid="link-modal-url" onClick={(e) => e.target.select()} />
                    <Button size="sm" onClick={() => handleCopyUrl(linkModal.url)} className="gap-1 shrink-0 bg-[#0066CC] hover:bg-[#0052A3]" data-testid="link-modal-copy">
                      <Copy className="w-3.5 h-3.5" /> Copy
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">Users must select their email on the form if opened via this generic link.</p>
                </div>

                {/* Per-user links */}
                {linkModal.users && linkModal.users.length > 0 && (
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Per-User Links (auto-fills name &amp; email)</Label>
                    <div className="mt-2 space-y-2 max-h-52 overflow-y-auto">
                      {linkModal.users.map((u) => {
                        const userUrl = `${linkModal.url}?email=${encodeURIComponent(u.email)}`;
                        return (
                          <div key={u.email} className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
                              <p className="text-xs text-slate-400 font-mono truncate">{userUrl}</p>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => handleCopyUrl(userUrl)} className="gap-1 shrink-0" data-testid={`copy-user-link-${u.email}`}>
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {viewFeedback && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewFeedback(null)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="feedback-detail-modal">
              <div className="flex items-center justify-between p-5 border-b">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{viewType === "sales" ? viewFeedback.user_name : viewFeedback.prospect_name || "Unknown"}</h3>
                  <p className="text-sm text-slate-500">{viewFeedback.user_email} {viewFeedback.campaign_title && `| ${viewFeedback.campaign_title}`}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setViewFeedback(null)}><X className="w-5 h-5" /></Button>
              </div>
              <div className="p-5 overflow-y-auto max-h-[65vh] space-y-3">
                {Object.entries(viewType === "sales" ? SALES_Q_LABELS : CUSTOMER_Q_LABELS).map(([key, label]) => {
                  const val = viewFeedback.responses?.[key];
                  if (!val || (Array.isArray(val) && val.length === 0)) return null;
                  const rk = viewType === "sales" ? SALES_RATING_KEYS : CUSTOMER_RATING_KEYS;
                  return (
                    <div key={key} className="flex flex-col gap-1 p-3 rounded-lg bg-slate-50">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
                      {rk.includes(key) ? <RatingBadge value={val} /> : Array.isArray(val) ? (
                        <ul className="text-sm text-slate-800 space-y-1">{val.map((item, i) => <li key={`${key}-${i}-${String(item).slice(0, 15)}`}>• {item}</li>)}</ul>
                      ) : <p className="text-sm text-slate-800 leading-relaxed">{val}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
